import React from 'react'
import { useNavigate } from 'react-router-dom'
import { FunnelIcon, PaletteIcon, ChartBarIcon } from '@phosphor-icons/react'
import { ROUTES } from '../../../router/routes'
import { EmptyDataset } from '../../EmptyDataset/EmptyDataset'
import { ScoopSpinner } from '../../ui'
import { ScoopCenter, ScoopText } from '../../ui'
import { useExplorer } from '../../../context/explorer'
import { ExplorerHeader } from './ExplorerHeader'
import { ExplorerLayout } from './ExplorerLayout'
import { ExplorerSummary } from './ExplorerSummary'
import { DataTab } from './DrawerTabs/DataTab/DataTab'
import { ConfigTab } from './DrawerTabs/ConfigTab/ConfigTab'
import styles from './ExplorerV2.module.scss'

/**
 * Placeholder component for Filter tab (Stage 7).
 */
const FilterTabPlaceholder = () => (
    <div className={styles.placeholderTab}>
        <FunnelIcon size={48} className={styles.placeholderIcon} />
        <ScoopText className={styles.placeholderText}>Filter tab coming in Stage 7</ScoopText>
    </div>
)

/**
 * Placeholder component for Style tab (Stage 9).
 */
const StyleTabPlaceholder = () => (
    <div className={styles.placeholderTab}>
        <PaletteIcon size={48} className={styles.placeholderIcon} />
        <ScoopText className={styles.placeholderText}>Style tab coming in Stage 9</ScoopText>
    </div>
)

/**
 * Main content component for Explorer.
 * Uses ExplorerContext and renders the layout with header, drawers, and chart area.
 *
 * Separated from ExplorerV2 because useExplorer() can only be called
 * inside a component that is a child of ExplorerProvider.
 *
 * Stage 6 Refactoring Complete:
 * - Removed bridge pattern (summaryConfig, setSummaryConfig)
 * - DataTab now uses useExplorer() directly
 * - All child components get state/actions from context
 */
export const ExplorerContent = () => {
    const navigate = useNavigate()

    // Get data from ExplorerContext
    const { workspaceMetadata, workspaceLoading } = useExplorer()

    const handleGoToDataset = () => {
        navigate(ROUTES.SOURCES, { state: { openNewDataset: true } })
    }

    // Loading state
    if (workspaceLoading) {
        return (
            <div className={styles.explorerContainer}>
                <ExplorerHeader />
                <ScoopCenter className={styles.loadingState}>
                    <ScoopSpinner size={40} />
                </ScoopCenter>
            </div>
        )
    }

    // Empty state - no datasets
    if (!workspaceMetadata?.inboxes?.length) {
        return (
            <div className={styles.explorerContainer}>
                <ExplorerHeader />
                <div className={styles.emptyState}>
                    <EmptyDataset
                        title="Welcome to Explorer"
                        description="Dive deep into your data with Scoop's Explorer—analyze, slice, and summarize your dataset to create stunning visual summaries and uncover key insights."
                        iconComponent={
                            <div className={styles.emptyIconWrapper}>
                                <ChartBarIcon
                                    size={32}
                                    weight="duotone"
                                    className={styles.iconDark}
                                />
                            </div>
                        }
                        onClick={handleGoToDataset}
                    />
                </div>
            </div>
        )
    }

    /**
     * Render function for left drawer content.
     * DataTab no longer receives props - it uses useExplorer() internally.
     */
    const renderLeftContent = (tab) => {
        switch (tab) {
            case 'data':
                return <DataTab />
            case 'filters':
                return <FilterTabPlaceholder />
            default:
                return null
        }
    }

    /**
     * Render function for right drawer content.
     */
    const renderRightContent = (tab) => {
        switch (tab) {
            case 'properties':
                return <ConfigTab />
            case 'style':
                return <StyleTabPlaceholder />
            default:
                return null
        }
    }

    return (
        <div className={styles.explorerContainer}>
            <ExplorerHeader />
            <ExplorerLayout
                leftContent={renderLeftContent}
                rightContent={renderRightContent}
                centerContent={<ExplorerSummary />}
            />
        </div>
    )
}
